import gql from 'graphql-tag'
import { FetchResult } from '@apollo/client'
import creditCardType from 'credit-card-type'
import { apolloClient } from 'lib/core/services/magento/client'
import { getCreditCardCode } from 'lib/core/resources/helpers'
import { useCart } from '../useCart'
import { useCheckout } from '../../../modules/checkout/hooks/useCheckout'
import { IframeForm, IframeFormProps } from './components/IframeForm'

interface SetPaymentMethodParams {
  ccExpMonth: number
  ccExpYear: number
  ccLast4: string
  ccType: string
  saveCard: boolean
}

export interface CreatePayflowProTokenPayload {
  response_message: string
  iframe_url: string
  result: string
  result_code: string
  secure_token: string
  secure_token_id: string
}

interface CreatePayflowProTokenResponse {
  createPayflowProToken: CreatePayflowProTokenPayload
}

export interface CreditCardData {
  number: string
  expMonth: string
  expYear: string
  cvv: string
  saveCard: boolean
}

type SetPaymentMethodOnCartCreditCardDataMutation = (params: SetPaymentMethodParams) => Promise<FetchResult<any>>
type SetPaymentMethodOnCartCreditCardVaultMutation = (publicHash: string) => Promise<FetchResult<any>>
type CreatePayflowProTokenMutation = () => Promise<FetchResult<CreatePayflowProTokenResponse>>

export interface FetchPayflowProTokenParams {
  ccData?: CreditCardData
  ccHash?: string
}

interface UsePayflowPro {
  fetchPayflowProToken: (params: FetchPayflowProTokenParams) => Promise<CreatePayflowProTokenPayload | undefined>
  PayflowProIframe: (props: IframeFormProps) => JSX.Element | null
}

export const usePayflowPro = (): UsePayflowPro => {
  const { lockCheckout, unlockCheckout } = useCheckout()
  const { cartId } = useCart()

  const setPaymentMethodOnCartCreditCardData: SetPaymentMethodOnCartCreditCardDataMutation = async ({
    ccExpMonth,
    ccExpYear,
    ccLast4,
    ccType,
    saveCard
  }) => {
    const query = `
      mutation {
        setPaymentMethodOnCart (
          input: {
            cart_id: "${cartId}",
            payment_method: {
              code: "payflowpro"
              payflowpro: {
                cc_details: {
                  cc_exp_month: ${ccExpMonth}
                  cc_exp_year: ${ccExpYear}
                  cc_last_4: "${ccLast4}"
                  cc_type: "${ccType}"
                }
                is_active_payment_token_enabler: ${saveCard}
              }
            }
          }
        ) {
          cart {
            selected_payment_method {
              code
              title
            }
          }
        }
      }
    `

    return apolloClient.mutate({ mutation: gql`${query}` })
  }

  const setPaymentMethodOnCartCreditCardVault: SetPaymentMethodOnCartCreditCardVaultMutation = async (publicHash: string) => {
    const query = `
      mutation {
        setPaymentMethodOnCart (
          input: {
            cart_id: "${cartId}",
            payment_method: {
              code: "payflowpro_cc_vault"
              payflowpro_cc_vault: {
                public_hash: "${publicHash}"
              }
            }
          }
        ) {
          cart {
            selected_payment_method {
              code
              title
            }
          }
        }
      }
    `

    return apolloClient.mutate({ mutation: gql`${query}` })
  }

  const createPayflowProToken: CreatePayflowProTokenMutation = async () => {
    const query = `
      mutation {
        createPayflowProToken (
          input: {
            cart_id: "${cartId}"
          }
        ) {
          response_message
          iframe_url
          result
          result_code
          secure_token
          secure_token_id
        }
      }
    `

    return apolloClient.mutate({ mutation: gql`${query}` })
  }

  const fetchPayflowProToken = async (params: FetchPayflowProTokenParams) => {
    lockCheckout()
    const { ccData, ccHash } = params
    try {
      if (ccHash) {
        await setPaymentMethodOnCartCreditCardVault(ccHash)
      } else if (ccData) {
        const [creditCardData] = creditCardType(ccData.number)
        await setPaymentMethodOnCartCreditCardData({
          ccType: getCreditCardCode(creditCardData?.type),
          ccLast4: ccData.number.substr(-4),
          ccExpYear: parseInt(ccData.expYear),
          ccExpMonth: parseInt(ccData.expMonth),
          saveCard: ccData.saveCard
        })
        const tokens = await createPayflowProToken()
        const data = tokens?.data?.createPayflowProToken
        if (!data) {
          throw new Error('Invalid Tokens')
        }
        return data
      } else {
        throw new Error('Invalid payment method.')
      }
      unlockCheckout()
    } catch (e) {
      unlockCheckout()
      throw e
    }
  }

  return {
    fetchPayflowProToken,
    PayflowProIframe: IframeForm
  }
}
